import React from 'react';
import { Box, Button, Container, Grid, IconButton, Link, Typography } from '@material-ui/core';

const footerFloatingKeyframes = `
@keyframes footerFloat {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-15px) rotate(3deg); }
}
@keyframes footerDrift {
    0%, 100% { transform: translate(0px, 0px); }
    33% { transform: translate(12px, -10px); }
    66% { transform: translate(-8px, -18px); }
}
@keyframes footerPulse {
    0%, 100% { opacity: 0.1; transform: scale(1); }
    50% { opacity: 0.2; transform: scale(1.08); }
}
`;

const renderBackgroundLayers = (layers = []) => {
    if (!Array.isArray(layers) || !layers.length) {
        return null;
    }

    return (
        <>
            <style>{footerFloatingKeyframes}</style>
            {layers.map((layer, index) => (
                <Box
                    key={`footer-bg-layer-${index}`}
                    className={layer.className || undefined}
                    style={layer.styles || {}}
                />
            ))}
        </>
    );
};

const FooterMultiZone = ({ sectionConfig = {}, layoutContext = {} }) => {
    const { getIcon = () => null, functionMap = {} } = layoutContext;
    const sections = sectionConfig.sections || [];
    const navigation = sectionConfig.navigation || [];
    const social = sectionConfig.social || [];
    const legal = sectionConfig.legal || [];
    const logo = sectionConfig.logo || {};
    const buttons = sectionConfig.buttons || [];

    const hasBackgroundLayers = Array.isArray(sectionConfig.backgroundLayers) && sectionConfig.backgroundLayers.length > 0;
    const footerStyles = { ...(sectionConfig.styles || {}) };
    if (hasBackgroundLayers) {
        footerStyles.position = footerStyles.position || 'relative';
        footerStyles.overflow = footerStyles.overflow || 'hidden';
    }

    const handleNavigationClick = (navItem = {}) => {
        if (navItem.action && typeof functionMap[navItem.action] === 'function') {
            functionMap[navItem.action]();
            return;
        }
        if (navItem.href) {
            window.open(navItem.href, navItem.target || '_blank', 'noopener');
        }
    };

    const handleButtonClick = (button = {}) => {
        if (button.action && typeof functionMap[button.action] === 'function') {
            functionMap[button.action](button.actionPayload);
            return;
        }
        if (button.href || button.url) {
            window.open(button.href || button.url, button.target || '_blank', 'noopener');
        }
    };

    const handleLinkNavigation = (link = {}) => {
        if (link.action && typeof functionMap[link.action] === 'function') {
            functionMap[link.action]();
            return;
        }
        const targetUrl = link.href || link.url;
        if (targetUrl) {
            window.open(targetUrl, link.target || '_blank', 'noopener');
        }
    };

    const renderLogo = () => {
        if (!logo) {
            return null;
        }
        return (
            <Box style={sectionConfig.logoContainerStyles || {}}>
                {logo.url && (
                    <Box component="img" src={logo.url} alt={logo.alt || logo.title || 'footer-logo'} style={logo.imageStyles || { height: 48 }} />
                )}
                {logo.title && (
                    <Typography variant={logo.variant || 'h6'} style={logo.titleStyles || logo.styles || {}}>
                        {logo.title}
                    </Typography>
                )}
            </Box>
        );
    };

    const renderNavigation = () => {
        if (!navigation.length) {
            return null;;
        }
        return (
            <Box style={sectionConfig.navigationStyles || { display: 'flex', gap: 16 }}>
                {navigation.map((navItem, idx) => (
                    <Box
                        key={`footer-nav-${idx}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleNavigationClick(navItem)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                handleNavigationClick(navItem);
                            }
                        }}
                        style={{ cursor: 'pointer', ...(sectionConfig.navLinkStyles || {}), ...(navItem.styles || {}) }}
                    >
                        {navItem.text}
                    </Box>
                ))}
            </Box>
        );
    };

    const renderButtons = () => {
        if (!buttons.length) {
            return null;
        }
        return (
            <Box style={sectionConfig.buttonContainerStyles || { display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {buttons.map((button, idx) => (
                    <Button
                        key={`footer-button-${idx}`}
                        variant={button.variant || 'contained'}
                        onClick={() => handleButtonClick(button)}
                        style={button.styles || {}}
                        startIcon={button.icon ? getIcon(button.icon) : undefined}
                        endIcon={button.endIcon ? getIcon(button.endIcon) : undefined}
                    >
                        {button.text || button.label}
                    </Button>
                ))}
            </Box>
        );
    };

    const renderSocialIcons = () => {
        if (!social.length) {
            return null;
        }
        return (
            <Box style={{ display: 'flex', gap: 12, ...(sectionConfig.socialStyles || {}) }}>
                {social.map((item, idx) => (
                    <IconButton
                        key={`footer-social-${idx}`}
                        href={item.url}
                        target={item.target || '_blank'}
                        rel="noopener noreferrer"
                        style={sectionConfig.socialIconStyles || {}}
                    >
                        {getIcon(item.icon) || item.label || item.icon}
                    </IconButton>
                ))}
            </Box>
        );
    };

    const renderLegalLinks = () => {
        if (!legal.length) {
            return null;
        }
        return (
            <Box style={sectionConfig.legalLinksStyles || { display: 'flex', gap: 16 }}>
                {legal.map((linkItem, idx) => (
                    <Link
                        key={`footer-legal-${idx}`}
                        href={linkItem.url}
                        target={linkItem.target || '_blank'}
                        rel="noopener noreferrer"
                        color={linkItem.color || 'inherit'}
                        style={{ textDecoration: 'none', ...(sectionConfig.legalLinkStyles || {}), ...(linkItem.styles || {}) }}
                    >
                        {linkItem.text}
                    </Link>
                ))}
            </Box>
        );
    };

    const renderLegacySections = () => {
        if (!sections.length) {
            return null;
        }
        return (
            <Grid container spacing={sectionConfig.grid?.spacing || 4}>
                {sections.map((section, idx) => (
                    <Grid
                        item
                        xs={sectionConfig.gridItem?.xs || 12}
                        sm={sectionConfig.gridItem?.sm || 6}
                        md={sectionConfig.gridItem?.md || 3}
                        key={`footer-section-${idx}`}
                    >
                        {(section.heading || section.title) && (
                            <Box style={section.titleContainerStyles || {}}>
                                {section.badge && (
                                    <Box style={section.badgeStyles || {}}>
                                        {section.badge.url ? (
                                            <Box
                                                component="img"
                                                src={section.badge.url}
                                                alt={section.badge.alt || 'footer-badge'}
                                                style={section.badge.imageStyles || {}}
                                            />
                                        ) : (
                                            <Typography style={section.badge.textStyles || {}}>
                                                {section.badge.text}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                                <Typography
                                    variant={section.headingVariant || section.titleVariant || 'subtitle1'}
                                    style={section.headingStyles || section.titleStyles || sectionConfig.sectionTitleStyles || {}}
                                >
                                    {section.heading || section.title}
                                </Typography>
                            </Box>
                        )}
                        {Array.isArray(section.links) && section.links.length > 0 && (
                            <Box style={section.linkContainerStyles || sectionConfig.linkContainerStyles || {}}>
                                {section.links.map((link, linkIdx) => (
                                    <Box
                                        key={`footer-link-${linkIdx}`}
                                        role={link.action || link.href || link.url ? 'button' : undefined}
                                        tabIndex={link.action || link.href || link.url ? 0 : undefined}
                                        onClick={() => handleLinkNavigation(link)}
                                        onKeyDown={(event) => {
                                            if ((event.key === 'Enter' || event.key === ' ') && (link.action || link.href || link.url)) {
                                                event.preventDefault();
                                                handleLinkNavigation(link);
                                            }
                                        }}
                                        style={{
                                            cursor: link.action || link.href || link.url ? 'pointer' : 'default',
                                            ...(section.linkStyles || sectionConfig.linkStyles || {}),
                                            ...(link.styles || {})
                                        }}
                                    >
                                        {link.text || link.label}
                                    </Box>
                                ))}
                            </Box>
                        )}
                        {section.description && (
                            <Typography variant={section.descriptionVariant || 'body2'} style={section.descriptionStyles || {}}>
                                {section.description}
                            </Typography>
                        )}
                        {Array.isArray(section.contact) && section.contact.length > 0 && (
                            <Box style={section.contactContainerStyles || sectionConfig.contactContainerStyles || {}}>
                                {section.contact.map((contactLine, contactIdx) => (
                                    <Typography
                                        key={`footer-contact-${contactIdx}`}
                                        variant={section.contactVariant || sectionConfig.typography?.contact?.variant || 'body2'}
                                        style={section.contactStyles || sectionConfig.contactStyles || {}}
                                    >
                                        {contactLine}
                                    </Typography>
                                ))}
                            </Box>
                        )}
                        {section.social && section.social.length > 0 && (
                            <Box style={{ display: 'flex', gap: 8, ...(section.socialContainerStyles || {}) }}>
                                {section.social.map((socialItem, socialIdx) => (
                                    <IconButton
                                        key={`footer-social-legacy-${socialIdx}`}
                                        href={socialItem.href}
                                        target={socialItem.target || '_blank'}
                                        rel="noopener noreferrer"
                                        style={socialItem.iconButtonStyles || {}}
                                    >
                                        {getIcon(socialItem.icon) || socialItem.icon}
                                    </IconButton>
                                ))}
                            </Box>
                        )}
                    </Grid>
                ))}
            </Grid>
        );
    };

    return (
        <Box id="footer-section" component="footer" style={footerStyles}>
            {renderBackgroundLayers(sectionConfig.backgroundLayers)}
            <Container
                maxWidth={sectionConfig.container?.maxWidth || 'lg'}
                style={{ position: 'relative', zIndex: 1, ...(sectionConfig.container?.styles || {}) }}
            >
                {sections.length > 0 ? (
                    <>
                        {renderLegacySections()}
                        {(sectionConfig.bottomNote || sectionConfig.copyright || legal.length > 0) && (
                            <Box style={sectionConfig.bottomSectionWrapperStyles || { marginTop: 32 }}>
                                {sectionConfig.bottomDividerStyles && (
                                    <Box style={sectionConfig.bottomDividerStyles} />
                                )}
                                <Box style={sectionConfig.bottomSectionStyles || { display: 'flex', flexWrap: 'wrap', gap: 24, marginTop: 24 }}>
                                    {(sectionConfig.bottomNote || sectionConfig.copyright) && (
                                        <Typography style={sectionConfig.bottomNoteStyles || {}}>
                                            {sectionConfig.bottomNote || sectionConfig.copyright}
                                        </Typography>
                                    )}
                                    {renderLegalLinks()}
                                </Box>
                            </Box>
                        )}
                    </>
                ) : (
                    <>
                        <Box style={sectionConfig.topSectionStyles || { display: 'flex', flexWrap: 'wrap', gap: 24 }}>
                            {renderLogo()}
                            {renderNavigation()}
                            {renderButtons()}
                            {renderSocialIcons()}
                        </Box>
                        <Box style={sectionConfig.bottomSectionStyles || { display: 'flex', flexWrap: 'wrap', gap: 24, marginTop: 24 }}>
                            {(sectionConfig.bottomNote || sectionConfig.copyright) && (
                                <Typography style={sectionConfig.bottomNoteStyles || {}}>
                                    {sectionConfig.bottomNote || sectionConfig.copyright}
                                </Typography>
                            )}
                            {renderLegalLinks()}
                        </Box>
                    </>
                )}
            </Container>
        </Box>
    );
};

export default FooterMultiZone;
